import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import Select, { SelectOption } from "@/components/select"
import Topbar from "@/components/topbar"
import { Icon } from "@/icons/icon"
import { UnixTimestamp, useDisableBack } from "@/lib/utils"
import { logout } from "@/services/auth"
import { getShifts } from "@/services/session"
import { handleNetworkError } from "@/services/base"
import useStore from "@/store"
import { ShiftDetails, ShiftStatus } from "@/store/session"
import { UserSlice } from "@/store/user"
import { Process, Shift } from "@/types/user"
import { HTTPError } from "ky"
import { posthog } from "@/lib/posthog"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import { parse } from "date-fns"
import { trim } from "lodash"
import { LabelValue } from "@/types/common"
import CheckboxGroup from "@/components/checkbox-group"
import { addProcesses, startShift } from "@/services/session"
import { preloadOfflineBundle } from "@/services/ipqc"

const PageState = {
  Disabled: "Disabled",
  Loading: "Loading",
  Enabled: "Enabled",
} as const

export default function OnboardShiftPage() {
  const navigate = useNavigate()
  const { employee, ...userState } = useStore((state) => state.user)
  const sessionStore = useStore.use.session()
  const [processes, setProcesses] = useState<LabelValue[]>([])
  const [shifts, setShifts] = useState<SelectOption[]>([])
  const [shiftId, setShiftId] = useState(sessionStore.shift.id)
  const [processIds, setProcessIds] = useState<string[]>(
    sessionStore.session.processes.map((item) => item.processId)
  )
  const [pageState, setPageState] = useState<string>(PageState.Disabled)
  const setUserState = useStore.use.setUserState()

  useDisableBack()
  useInit()

  function useInit() {
    useEffect(() => {
      void init()
    }, [])
  }

  function parseSessionDetails(shift: SelectOption): ShiftDetails | null {
    try {
      const parts = shift.label.split(" ").map((item) => trim(item, "() "))
      if (parts.length !== 4) {
        return null
      }

      const [name, startTime, __, endTime] = parts
      const timeFormat = "HH:mm"

      const start = parse(startTime, timeFormat, new UnixTimestamp())
      const end = parse(endTime, timeFormat, new UnixTimestamp())

      const details: ShiftDetails = {
        id: shiftId,
        name: name,
        startedAt: start.toISOString(),
        endedAt: end.toISOString(),
      }

      return details
    } catch (err) {
      console.error(err)

      return null
    }
  }

  async function init() {
    try {
      initProcesses(userState.processes)
      initShifts(userState.shifts)
      setPageState(PageState.Loading)

      const res = await getShifts()

      const newState: UserSlice["user"] = {
        employee: {
          code: employee.code,
          ...res.employee,
        },
        plant: {
          id: res.plant.plantId,
          name: res.plant.name,
          city: res.plant.city,
        },
        shifts: res.shifts.map((item) => ({
          id: item.shiftId,
          name: item.name,
          startTime: item.startTime,
          endTime: item.endTime,
        })),
        processes: res.processes.map((item) => ({
          id: item.processId,
          name: item.name,
          type: item.type,
        })),
        token: userState.token,
      }
      setUserState(newState)

      initProcesses(newState.processes)
      initShifts(newState.shifts)
      setPageState(PageState.Enabled)

      if (sessionStore.session.status === ShiftStatus.Started) {
        await navigate("/manage-processes")
      }
    } catch (err) {
      posthog.captureException(err)
      const handled = await handleNetworkError(err, navigate)
      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      toast.error("Error fetching shifts")
    }
  }

  function initProcesses(processes: Process[]) {
    setProcesses(
      processes.map((item) => ({
        id: item.id,
        label: item.name,
        value: item.id,
      }))
    )
  }

  function initShifts(shifts: Shift[]) {
    setShifts(
      shifts.map((item) => ({
        label: `${item.name} (${item.startTime} - ${item.endTime})`,
        value: item.id,
      }))
    )
    setShiftId("")
  }

  function handleSelectShift(value: string) {
    setShiftId(value)
  }

  function handleSelectProcess(value: string[]) {
    setProcessIds(value)
  }

  async function handleStartShift() {
    try {
      const shift = shifts.find((shift) => shift.value === shiftId)
      if (!shift) {
        toast.error("Shift cannot be found")
        posthog.captureException(new Error("Shift cannot be found"))

        return
      }

      const details = parseSessionDetails(shift)
      if (!details) {
        toast.error("Invalid shift name format")
        posthog.captureException(new Error("Invalid shift name format"))

        return
      }
      sessionStore.setShiftDetails(details)

      setPageState(PageState.Loading)

      if (!sessionStore.session.id) {
        await startShift({ shiftId: shiftId })
      }

      await addProcesses(processIds)

      try {
        await preloadOfflineBundle()
      } catch (bundleErr) {
        console.error("Failed to preload offline IPQC bundle:", bundleErr)
        toast.error(
          "Shift started, but IPQC offline bundle could not be cached yet."
        )
      }

      await navigate("/manage-processes")
    } catch (err) {
      console.error(err)
      const handled = await handleNetworkError(err, navigate)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      toast.error("Starting shift failed. Please contact support")
    } finally {
      setPageState(PageState.Enabled)
    }
  }

  function canStartShift() {
    return !!(shiftId && processIds.length) && pageState === PageState.Enabled
  }

  return (
    <div className="relative h-dvh">
      <Topbar className="sticky" title="Unit Quality" subtitle={employee.name}>
        <Button variant="outline" onClick={logout}>
          <Icon name="Power" className="h-8 w-auto text-button-black" />
        </Button>
      </Topbar>

      <div className="p-4 pt-8">
        <h2 className="text-button-black text-2xl font-semibold">
          Welcome {employee.name}!
        </h2>

        <Select
          className="mt-6"
          label="Shift Duration"
          options={shifts}
          disabled={pageState !== PageState.Enabled}
          value={shiftId}
          onChange={handleSelectShift}
        />

        <CheckboxGroup
          className="mt-4"
          label="Select Process"
          options={processes}
          disabled={pageState !== PageState.Enabled}
          value={processIds}
          onChange={handleSelectProcess}
        />
      </div>

      <Bottombar className="absolute bottom-0  flex gap-4">
        <Button
          className="grow"
          disabled={!canStartShift()}
          isLoading={pageState === PageState.Loading}
          onClick={handleStartShift}
        >
          Start Shift
        </Button>
      </Bottombar>
    </div>
  )
}
